"use client";

import { DismissableAd } from "@/components/pages/DismissableAd";
import type { PlacementDecision } from "@/lib/placements/decision";
import { UNIT_WIDTH } from "@/lib/placements/floating";
import { Placement } from "./Placement";
import { useViewportAtLeast } from "./useViewportAtLeast";

/**
 * A page type's RIGHT slot on a page that has no right-hand column: fixed to
 * the viewport in the gutter, outside the page grid, so it never competes with
 * a pinned sidebar. `minViewport` is one of the lib/placements/floating.ts
 * constants, chosen by the render site for what the unit has to clear there
 * (the thresholds live in a plain module because the render sites are server
 * components and cannot take a value from this "use client" file — they would
 * get a client reference). The server renders nothing and the client mounts
 * the unit after hydration when the viewport qualifies, so the impression is
 * sent only for a unit actually on screen. Pages WITH a column (the module
 * details) use ColumnPlacement instead.
 * Session-dismissible like every surface — the frame's own label line carries
 * the "Ad" label and the always-visible close, and the dismiss wraps the fixed
 * box itself, so nothing stays pinned to the viewport afterwards. The unit is
 * a small card (surface, hairline, soft shadow): it floats over whatever the
 * page has there, and a label line with no ground of its own vanishes on a
 * photo. Sits below the fixed site header. Viewability needs nothing special:
 * the observer measures fixed elements against the viewport like any other.
 */
export function FloatingPlacement({
  decision,
  minViewport,
}: {
  decision?: PlacementDecision | null;
  minViewport: number;
}) {
  const wide = useViewportAtLeast(minViewport);
  if (!decision || !wide) return null;

  return (
    <DismissableAd storageKey={decision.slot}>
      <div
        className="ring-border/60 fixed right-2 top-[calc(var(--marketing-header-h)+var(--reader-tools-h,0px)+1rem)] z-20 rounded-card-lg bg-card p-2 shadow-lg ring-1"
        style={{ width: UNIT_WIDTH }}
      >
        <Placement decision={decision} />
      </div>
    </DismissableAd>
  );
}
